import { Box, Card, CardContent, Skeleton, Stack, Typography } from "@mui/material";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { LabelValue } from "@/features/dashboard/api/get-dashboard-data";

// Colors drawn from the app's established palette
const PALETTE = [
    "#635bff", // neonBlue.500
    "#15b79f", // kepple.500
    "#fb9c0c", // california.500
    "#04aad6", // shakespeare.500
    "#f04438", // redOrange.500
    "#432ad8", // neonBlue.700
    "#107569", // kepple.700
    "#b84d05", // california.700
    "#0d6d91", // shakespeare.700
    "#667085", // stormGrey.500
];

interface TooltipData { active?: boolean; payload?: { name?: string; value?: number }[]; }
function CustomTooltip({ active, payload }: TooltipData) {
    if (!active || !payload?.length) return null;
    return (
        <Box sx={{
            bgcolor: "grey.900", color: "common.white",
            px: 1.5, py: 1, borderRadius: "8px",
            fontSize: "0.8125rem", fontWeight: 500,
            boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
        }}>
            <Box sx={{ color: "grey.400", fontSize: "0.75rem", mb: 0.25 }}>{payload[0].name}</Box>
            <Box>{payload[0].value?.toLocaleString("es-PY")}</Box>
        </Box>
    );
}

function EmptyState() {
    return (
        <Box sx={{ height: 220, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Typography variant="body2" color="text.disabled">Sin datos para el período</Typography>
        </Box>
    );
}

interface Props {
    title: string;
    data?: LabelValue[];
    loading: boolean;
    showCount?: boolean;
}

export default function PieChartCard({ title, data, loading, showCount = false }: Props) {
    const isEmpty = !loading && (!data || data.length === 0);
    const chartData = data?.map((d) => ({ name: d.label, value: d.value }));

    return (
        <Card sx={{ height: "100%" }}>
            <CardContent sx={{ pb: "16px !important" }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mb: 1.5 }}>
                    {title}
                </Typography>

                {loading ? (
                    <Skeleton variant="rectangular" height={220} sx={{ borderRadius: "8px" }} />
                ) : isEmpty ? (
                    <EmptyState />
                ) : (
                    <Box>
                        <ResponsiveContainer width="100%" height={180}>
                            <PieChart>
                                <Pie
                                    data={chartData}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={48}
                                    outerRadius={80}
                                    strokeWidth={2}
                                    stroke="var(--mui-palette-background-paper)"
                                >
                                    {chartData?.map((_, i) => (
                                        <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                                    ))}
                                </Pie>
                                <Tooltip content={<CustomTooltip />} />
                            </PieChart>
                        </ResponsiveContainer>

                        {/* Legend */}
                        <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                            {chartData?.slice(0, 5).map((entry, i) => {
                                const total = chartData.reduce((s, d) => s + d.value, 0);
                                const pct = total > 0 ? Math.round((entry.value / total) * 100) : 0;
                                return (
                                    <Stack key={i} direction="row" alignItems="center" justifyContent="space-between">
                                        <Stack direction="row" alignItems="center" spacing={0.75}>
                                            <Box sx={{
                                                width: 8, height: 8, borderRadius: "50%",
                                                bgcolor: PALETTE[i % PALETTE.length], flexShrink: 0,
                                            }} />
                                            <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 140 }}>
                                                {entry.name}
                                            </Typography>
                                        </Stack>
                                        <Typography variant="caption" fontWeight={500} color="text.primary" sx={{ whiteSpace: "nowrap" }}>
                                            {showCount ? `${entry.value.toLocaleString("es-PY")} (${pct}%)` : `${pct}%`}
                                        </Typography>
                                    </Stack>
                                );
                            })}
                            {(chartData?.length ?? 0) > 5 && (
                                <Typography variant="caption" color="text.disabled">
                                    +{(chartData?.length ?? 0) - 5} más
                                </Typography>
                            )}
                        </Stack>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
}
